/*
 * A LEAD'S TIME, ALWAYS BESIDE MINE: "07:05 ET for Dana · 14:05 your time".
 *
 * The house rule is that a lead's times are never shown in Israel time, and never
 * alone either: Gabriel reads every time on the record twice, once to know what
 * the hour is for them ("is it 7am in Boston?") and once to know when it was in
 * his own day. So the record has one place that writes both, in one order,
 * {theirs} · {yours}, and every line that prints a lead's time calls it.
 *
 * WHERE THE ZONES COME FROM. Neither is guessed here.
 *   - theirs: `local_zone` on GET /contacts/:id and on every company contact,
 *     { tz, abbr, city, from } from contactZone() in src/email/timezones.js. It is
 *     null when nobody knows (a US company with no city has no clock), and then
 *     this module prints only Gabriel's half. It never falls back to his zone:
 *     a lead with no zone is a lead whose hour we do not know, and printing his
 *     hour as theirs is exactly the mistake the rule exists to stop.
 *   - mine: `my_zone` on GET /companies/:id/actions, { tz, abbr }, the zone of
 *     the after-hours schedule. When the payload is missing (no company), the
 *     browser's own zone stands in, which is the zone he is reading in.
 *
 * ABBREVIATIONS SAY "ET", NOT "EDT" (Gabriel, 2026-09-30: "nobody says that").
 * GENERIC_ZONE is the server's own table in src/email/timezones.js, copied
 * because the client build cannot import from src/; test/record-zones.test.js
 * reads both files and fails when they drift.
 */
import { clock, toDate } from "./format.mjs";

export const GENERIC_ZONE = {
  EDT: "ET", EST: "ET",
  CDT: "CT", CST: "CT",
  MDT: "MT", MST: "MT",
  PDT: "PT", PST: "PT",
  AKDT: "AKT", AKST: "AKT",
  ADT: "AT", AST: "AT",
  NDT: "NT", NST: "NT",
};

/** The browser's zone, the reader's own clock. */
export function browserZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** A zone as a person writes it: "ET", "PT", "BST", "GMT+3". Null for a bad zone. */
export function zoneAbbr(tz, at = new Date()) {
  if (!tz) return null;
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(toDate(at) || new Date())
      .find((p) => p.type === "timeZoneName");
    if (!part) return null;
    return GENERIC_ZONE[part.value] || part.value;
  } catch {
    return null;
  }
}

/** Minutes east of UTC for `tz` at `at`, or null. */
function offsetMinutes(tz, at) {
  try {
    const d = toDate(at) || new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).formatToParts(d);
    const get = (t) => Number((parts.find((p) => p.type === t) || {}).value);
    const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"));
    return Math.round((asUtc - Math.floor(d.getTime() / 60000) * 60000) / 60000);
  } catch {
    return null;
  }
}

/** Do the two zones read the same clock at `at`? "Israel · same as yours". */
export function sameClock(a, b, at = new Date()) {
  if (!a || !b) return false;
  if (a === b) return true;
  const x = offsetMinutes(a, at);
  const y = offsetMinutes(b, at);
  return x !== null && x === y;
}

/**
 * The two zones the record writes with, normalised once: `theirs` is the lead's
 * { tz, abbr, city, from } or null, `mine` is { tz, abbr } and never null.
 */
export function recordZones({ localZone = null, myZone = null } = {}) {
  const theirs = localZone && localZone.tz ? localZone : null;
  const mineTz = (myZone && myZone.tz) || browserZone();
  return { theirs, mine: { tz: mineTz, abbr: (myZone && myZone.abbr) || zoneAbbr(mineTz) } };
}

/**
 * One person's zones. A line about Ella on Priya's record reads Ella's clock: the
 * record's `theirs` is only ever the person on screen. Every company contact
 * carries its own `local_zone` (GET /companies/:id, withLocalZones), null when
 * nobody knows it, and then theirs is null too: printing Priya's hour as Ella's
 * is the same guess the house rule forbids for Gabriel's. A person the payload
 * says nothing about (no local_zone key at all: a stub, an older server) reads the
 * record's zones.
 */
export function personZones(person, zones) {
  const z = zones || {};
  if (!person || typeof person !== "object" || !("local_zone" in person)) return z;
  const own = person.local_zone && person.local_zone.tz ? person.local_zone : null;
  return { ...z, theirs: own };
}

/** "07:05 ET": a time in their zone with the zone's name, or null when unknown. */
export function theirClock(ts, theirs) {
  if (!theirs || !theirs.tz || !toDate(ts)) return null;
  const abbr = zoneAbbr(theirs.tz, ts) || theirs.abbr || null;
  const t = clock(ts, { timeZone: theirs.tz });
  return abbr ? `${t} ${abbr}` : t;
}

/**
 * ONE MOMENT, BOTH ZONES, THEIRS FIRST.
 *
 *   bothZones(ts, zones, { name: "Dana" })
 *     → { text: "07:05 ET for Dana · 14:05 your time", theirs: "07:05 ET", yours: "14:05" }
 *
 * Without a name the first half reads "07:05 ET". When their clock is the same
 * as his (Pluto in Israel) the one hour is both, and is printed once: "16:00"
 * (`same` is true for a caller that wants to say so). When their zone is unknown only his
 * half is printed, "14:05 your time", and `theirs` is null so a caller that must
 * not show a lone time (a slot offered in an email) can tell.
 */
export function bothZones(ts, zones, { name = null } = {}) {
  if (!toDate(ts)) return null;
  const z = zones && zones.mine ? zones : recordZones(zones || {});
  const yours = clock(ts, { timeZone: z.mine.tz });
  if (!z.theirs) return { text: `${yours} your time`, theirs: null, yours };
  // Their clock is his (Pluto in Israel): one hour, said once, is both zones.
  if (sameClock(z.theirs.tz, z.mine.tz, ts)) return { text: yours, theirs: yours, yours, same: true };
  const theirs = theirClock(ts, z.theirs);
  return { text: `${name ? `${theirs} for ${name}` : theirs} · ${yours} your time`, theirs, yours };
}

/**
 * THEIR TIME RIGHT NOW: "07:20 in Boston (ET) · 14:20 yours".
 *
 * `place` names where they are when the zone came from the country (the zone
 * payload carries a city only when one decided it): "Israel · same as yours".
 * Null when their zone is unknown, and then the line is left out entirely.
 */
export function nowLine(zones, { now = new Date(), place = null } = {}) {
  const z = zones && zones.mine ? zones : recordZones(zones || {});
  if (!z.theirs) return null;
  const where = z.theirs.city || place || null;
  if (sameClock(z.theirs.tz, z.mine.tz, now)) return where ? `${where} · same as yours` : "same as yours";
  const abbr = zoneAbbr(z.theirs.tz, now) || z.theirs.abbr || "";
  const theirs = clock(now, { timeZone: z.theirs.tz });
  const yours = clock(now, { timeZone: z.mine.tz });
  const head = where ? `${theirs} in ${where}${abbr ? ` (${abbr})` : ""}` : `${theirs}${abbr ? ` ${abbr}` : ""}`;
  return `${head} · ${yours} yours`;
}
